import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getAuctionView, auctionBid, getPlayerBios } from '../../data';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import {
    Typography, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip
} from '@material-ui/core';

const POSITION_COLORS = {
    'Batter': '#2196f3',
    'Bowler': '#4caf50',
    'All-Rounder': '#ff9800',
    'WK-Batter': '#9c27b0',
};

function AuctionView() {
    const { leagueID } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [bidDialogOpen, setBidDialogOpen] = useState(false);
    const [bidSquad, setBidSquad] = useState('');
    const [bidPlayerID, setBidPlayerID] = useState('');
    const [bidPrice, setBidPrice] = useState('');
    const [bidError, setBidError] = useState(null);
    const [playerBios, setPlayerBios] = useState([]);
    const [playerSearch, setPlayerSearch] = useState('');

    const loadData = async () => {
        try {
            setLoading(true);
            const result = await getAuctionView(leagueID);
            setData(result);
            setError(null);
            if (result.tournament) {
                const bios = await getPlayerBios(result.tournament);
                setPlayerBios(bios);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [leagueID]);

    const handleBidSubmit = async () => {
        try {
            setBidError(null);
            await auctionBid(leagueID, bidSquad, bidPlayerID, parseFloat(bidPrice));
            setBidDialogOpen(false);
            setBidSquad('');
            setBidPlayerID('');
            setBidPrice('');
            setPlayerSearch('');
            await loadData();
        } catch (err) {
            setBidError(err.message);
        }
    };

    const filteredPlayers = playerSearch.length >= 2
        ? playerBios.filter(p =>
            p.name.toLowerCase().includes(playerSearch.toLowerCase()) &&
            !(data?.squads || []).some(s => s.players.some(sp => sp.id === p.id))
        ).slice(0, 20)
        : [];

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <Typography color="error">{error}</Typography>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', marginBottom: 50 }}>
            <div style={{ flexBasis: '100%', marginBottom: 20 }}>
                <Typography variant="h6">{data.leagueName} - Auction</Typography>
                <div style={{ marginTop: 8 }}>
                    <Link to={`/leagues/${leagueID}/auction/log`}>
                        <Button variant="outlined" size="small">View Auction Log</Button>
                    </Link>
                    <Link to={`/leagues/${leagueID}`} style={{ marginLeft: 12 }}>
                        <Button variant="outlined" size="small">Back to League</Button>
                    </Link>
                    {data.isAdmin && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            style={{ marginLeft: 12 }}
                            onClick={() => setBidDialogOpen(true)}
                        >
                            Assign Player
                        </Button>
                    )}
                </div>
            </div>

            {data.squads.map(squad => (
                <TableContainer
                    component={Paper}
                    key={squad.name}
                    style={{ maxWidth: 600, margin: '8px 0' }}
                >
                    <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                            {squad.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {squad.squadSize} players | Spent: {squad.kittySpent} | Remaining: {squad.kittyRemaining}
                        </Typography>
                    </div>
                    {Object.entries(squad.byPosition).map(([pos, players]) => {
                        if (players.length === 0) return null;
                        return (
                            <div key={pos} style={{ padding: '4px 16px' }}>
                                <Chip
                                    label={`${pos} (${players.length})`}
                                    size="small"
                                    style={{
                                        backgroundColor: POSITION_COLORS[pos] || '#757575',
                                        color: '#fff',
                                        marginBottom: 4,
                                    }}
                                />
                                <Table size="small">
                                    <TableBody>
                                        {players.map(player => (
                                            <TableRow key={player.id}>
                                                <TableCell>{player.name}</TableCell>
                                                <TableCell>{player.team}</TableCell>
                                                <TableCell align="right">{player.price}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        );
                    })}
                </TableContainer>
            ))}

            <Dialog open={bidDialogOpen} onClose={() => setBidDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign Player to Squad</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth style={{ marginBottom: 16 }}>
                        <InputLabel id="squad-select-label">Squad</InputLabel>
                        <Select
                            labelId="squad-select-label"
                            value={bidSquad}
                            onChange={e => setBidSquad(e.target.value)}
                            data-testid="squad-select"
                        >
                            {(data?.squads || []).map(s => (
                                <MenuItem key={s.name} value={s.name}>{s.name} (remaining: {s.kittyRemaining})</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Search Player"
                        value={playerSearch}
                        onChange={e => setPlayerSearch(e.target.value)}
                        style={{ marginBottom: 8 }}
                    />
                    {filteredPlayers.length > 0 && (
                        <Paper style={{ maxHeight: 200, overflow: 'auto', marginBottom: 16 }}>
                            <Table size="small">
                                <TableBody>
                                    {filteredPlayers.map(p => (
                                        <TableRow
                                            key={p.id}
                                            hover
                                            selected={bidPlayerID === p.id}
                                            onClick={() => { setBidPlayerID(p.id); setPlayerSearch(p.name); }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>{p.name}</TableCell>
                                            <TableCell>{p.team}</TableCell>
                                            <TableCell>{p.pos}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    )}
                    {bidPlayerID && (
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            Selected: {playerBios.find(p => p.id === bidPlayerID)?.name || bidPlayerID}
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        label="Price"
                        type="number"
                        value={bidPrice}
                        onChange={e => setBidPrice(e.target.value)}
                    />
                    {bidError && (
                        <Typography color="error" style={{ marginTop: 8 }}>{bidError}</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBidDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleBidSubmit}
                        color="primary"
                        variant="contained"
                        disabled={!bidSquad || !bidPlayerID || !bidPrice}
                    >
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default AuctionView;
